/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, Drawer, Form, Input, Menu, Modal, Select, Space} from "antd";
import {useEffect, useState} from "react";
import {closeDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {patch, post, del} from "../core/request";
import {IconSet} from "../components/icons";
import {Dropdown} from "antd";
import {CustomButton} from "../components/buttons";

import {DeviceModel, DeviceType} from "../core/datamodel";

type Class = DeviceModel
const stack_selector = (state: any) => state.drawer.devicemodel
const uri = "devicemodels"
const key = "devicemodel"
const title = "Gerätemodell"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function DeviceModelDrawer(props: DrawerProps) {
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as Class);
    const [form] = Form.useForm();

    const isNew = () => !(!!object?.props?.uuid)


    useEffect(() => {
        const object = stack[stack.length - 1] as Class
        form.resetFields()
        form.setFieldsValue({
            ...object?.props,
        })
        setObject(object)
    }, [stack, form])

    const onFormFinish = () => {
        let values = form.getFieldsValue()

        object.update({
            ...values,
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const onDrawerClose = (e: any) => {
        if (e.type === 'click' &&
            (e.target as Element).className === "ant-drawer-mask"
        ) {
            Modal.confirm({
                title: "Fenster wirklich schließen?",
                content: "Alle Änderungen gehen verloren!",
                maskClosable: true,
                cancelText: 'Weiter bearbeiten',
                okText: 'Änderungen verwerfen',
                autoFocusButton: 'cancel',
                onOk: () => {
                    dispatch(closeDrawer(key));
                    return false;
                }
            })
        } else {
            dispatch(closeDrawer(key))
        }

    }

    const typeOptions = (
        <>
            {
                Object.keys(DeviceType).filter(k => typeof DeviceType[k as any] === "number").map(
                    (type) => <Select.Option key={type} value={type} children={type}/>
                )
            }
        </>
    )

    const form_layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 16},
    };

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
            requiredMark={true}
        >
            <Form.Item label="Name" name="name" required rules={[{required: true, message: "Bitte Namen eingeben!"}]}>
                <Input placeholder="Name"/>
            </Form.Item>

            <Form.Item label="Typ" name="type" required rules={[{required: true, message: "Bitte Typ wählen!"}]}>
                <Select placeholder={"Typ wählen"} allowClear>
                    {typeOptions}
                </Select>
            </Form.Item>

            <Form.Item name="manufacturer" label="Hersteller" required
                       rules={[{required: true, message: "Bitte Hersteller eingeben!"}]}>
                <Input name='manufacturer' placeholder="Hersteller"/>
            </Form.Item>

            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={onDrawerClose}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "delete",
                            danger: true,
                            disabled: !object?.props?.is_deletable,
                            onClick: onDelete,
                            icon: <IconSet type='delete'/>,
                            label: "Löschen"
                        },
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={onDrawerClose}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
        >
            {form_element}
        </Drawer>
    )
}


DeviceModelDrawer.defaultProps = {
    size: 'large'
}